// /services/pharma-bd-retainer — flagship Pharma BD Retainer page.
// Geography-agnostic productized offering with a Greater-China case study.
// Linked from /services. Designed to be sharable as a sales leave-behind URL.
import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import PublicNav from '@/components/PublicNav';
import PublicFooter from '@/components/PublicFooter';
import { useSEO } from '@/hooks/useSEO';
import { usePageTracker } from '@/hooks/usePageTracker';
import {
  ArrowRight, Search, BarChart3, Microscope, Calendar, Handshake, Database,
  CheckCircle2, MapPin, Globe, FileText, Layers, Target, Brain, Languages,
  Clock, ShieldCheck, TrendingUp, Beaker, Users, Award, Sparkles
} from 'lucide-react';

function useInView(threshold = 0.15) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = ref.current; if (!el) return;
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setInView(true); obs.disconnect(); } }, { threshold });
    obs.observe(el); return () => obs.disconnect();
  }, [threshold]);
  return [ref, inView];
}

// 6 core workstreams from the productized retainer
const WORKSTREAMS = [
  { icon: <Search />, title: 'Asset Scouting & Identification',
    desc: 'Continuous in-region scanning across regulatory filings, conference floors, technology-transfer offices, broker networks, and proprietary BD relationships — all calibrated to your stated therapeutic priorities.',
    out: 'Curated target list of 200+ companies in Months 1–2; refreshed weekly thereafter.' },
  { icon: <Microscope />, title: 'Asset Evaluation & 8-Dimension Scoring',
    desc: 'Every shortlisted asset receives a structured evaluation dossier covering clinical development, regulatory pathway, IP landscape, competitive positioning, commercial potential, and strategic fit. Scoring is transparent, with confidence levels stated.',
    out: '20–30 dossiers per year, 10–15 pages each.' },
  { icon: <BarChart3 />, title: 'Competitive Landscape Monitoring',
    desc: 'Daily tracking of approvals, trial starts, deal announcements, pricing decisions, and market-access shifts in your competitive zone. Distilled into weekly flashes and expanded in monthly intelligence reports.',
    out: '52 weekly flashes + 12 monthly reports + 4 quarterly strategic reviews per year.' },
  { icon: <Calendar />, title: 'Conference Floor Coverage',
    desc: 'On-site presence at four region-relevant congresses per year. Pre-event target lists, one-on-one meeting facilitation, same-day flash reports, post-event opportunity briefs.',
    out: '4 conferences fully covered, including travel and registration.' },
  { icon: <Handshake />, title: 'Partner Matching & Deal Facilitation',
    desc: 'Beyond intelligence, we actively introduce and facilitate. Initial outreach, due-diligence material exchange, term-sheet discussion support, ongoing liaison through to heads of agreement.',
    out: 'Hands-on engagement through deal close, not just at the scouting stage.' },
  { icon: <Database />, title: 'Platform Access & AI Research Copilot',
    desc: 'A dedicated dashboard on the ForesightRx platform with deal pipeline tracking, contact management, asset watchlists, and an AI-powered copilot that answers natural-language questions with sourced and cited evidence.',
    out: 'Live access for the entire BD team; no per-seat fees.' },
];

// Cadence ribbon — the headline number
const CADENCE = [
  { n: '52', label: 'Weekly flash updates' },
  { n: '12', label: 'Monthly intelligence reports' },
  { n: '4', label: 'Quarterly strategic reviews' },
  { n: '4', label: 'Conferences covered live' },
  { n: '20–30', label: 'Asset evaluation dossiers' },
  { n: '90+', label: 'Total structured deliverables / year' },
];

// 8-dimension scoring framework (referenced in the eval workstream)
const EIGHT_DIMENSIONS = [
  'Clinical development status',
  'Regulatory pathway',
  'Intellectual property',
  'Competitive positioning',
  'Commercial potential',
  'Strategic / portfolio fit',
  'Deal-readiness',
  'Risk / red flags',
];

// Why retainer beats subscription
const RETAINER_VS_SUB = [
  { icon: <Languages />, title: 'Local-language fluency',
    desc: 'Subscription databases rarely cover regional regulators (NMPA, PMDA, MFDS, CDSCO), local trial registries, or vernacular biotech channels in sufficient depth. Our team monitors them daily.' },
  { icon: <Calendar />, title: 'Conferences = where deals happen',
    desc: 'Subscriptions don\'t do on-site coverage; or they charge premium per-event fees. Four region-relevant conferences are included in your retainer.' },
  { icon: <Handshake />, title: 'Hands-on facilitation',
    desc: 'A subscription stops at delivering data. We make introductions, coordinate diligence, and facilitate term-sheet discussions through to heads of agreement.' },
  { icon: <ShieldCheck />, title: 'Predictable pricing',
    desc: 'Fixed quarterly retainer. No per-query fees. No surprise charges for an extra dossier or a sudden conference run.' },
];

const GEOGRAPHIES = [
  'Greater China', 'Japan', 'Korea', 'India', 'Southeast Asia',
  'Australia / New Zealand', 'European Union', 'LATAM', 'MENA', 'Global',
];

const ADDONS = [
  'Additional conference coverage (beyond 4 included)',
  'Extended asset dossiers with deep IP analysis',
  'HEOR / Real-World Evidence studies (via MarksMan Healthcare)',
  'KOL mapping & advisory board facilitation',
  'Regulatory pathway advisory (region-specific)',
  'Social-media & vernacular channel listening',
  'Deal facilitation success fee (on signed deals)',
  'Medical writing & publication support',
];

// Greater China case study sources
const CHINA_SOURCES = [
  { icon: <FileText />, title: 'NMPA & CDE filings',
    desc: 'Daily monitoring of new IND applications, clinical trial registrations, NDA submissions, and approval decisions.' },
  { icon: <Users />, title: 'Conference floor intelligence',
    desc: 'On-site presence at four major China-focused conferences per year with structured one-on-one meeting programmes.' },
  { icon: <ShieldCheck />, title: 'CNIPA patent databases',
    desc: 'Composition-of-matter, formulation, and method-of-use claims scanned in original Chinese, not English translations.' },
  { icon: <Brain />, title: 'WeChat & vernacular channels',
    desc: 'Biotech WeChat groups, Zhihu medical forums, Chinese-language industry newsletters — deal signals weeks before English coverage.' },
  { icon: <Beaker />, title: 'Academic TTOs',
    desc: 'Direct outreach to technology-transfer offices at Tsinghua, Peking, Fudan, Zhejiang, Shanghai Jiao Tong, and others.' },
  { icon: <Handshake />, title: 'Cross-border BD networks',
    desc: 'Established relationships with Chinese biotech BD executives, licensing intermediaries, and deal brokers in Shanghai, Beijing, Suzhou, Guangzhou.' },
  { icon: <Database />, title: 'Global partnering platforms',
    desc: 'Inova, Cortellis, Biotechgate — monitored continuously for newly listed Chinese assets seeking out-licensing partners.' },
  { icon: <Sparkles />, title: 'ForesightRx proprietary database',
    desc: '200+ Chinese biotech companies and 500+ China-origin assets already tracked in our living database.' },
];

const CHINA_CONFERENCES = [
  { name: 'ChinaBio Partnering Forum', city: 'Shanghai', month: 'April', detail: '700+ delegates from 500+ companies across 30 countries (ChinaBio, 2025).' },
  { name: 'CPHI & PMEC China', city: 'Shanghai', month: 'June', detail: 'Asia\'s largest pharma exhibition: 110,000+ professionals from 150+ countries (CPHI, 2026).' },
  { name: 'CPIC — China Pharmaceutical Innovation Conference', city: 'Shanghai', month: 'July', detail: 'Modelled after JPM Healthcare. Multinational pharma + global I-banks + 1,000+ Chinese innovators.' },
  { name: 'BioCentury China Healthcare Summit', city: 'Shanghai', month: 'November', detail: 'Curated by BioCentury, BayHelix, and McKinsey. Senior BD + innovation leadership.' },
];

export default function PharmaBDRetainerPage() {
  useSEO({
    title: 'Pharma BD Retainer · Asset Search, Evaluation & Monitoring · ForesightRx',
    description: 'A 12-month retainer that replaces subscription databases, conference scouts, and BD consultants with one dedicated team. 90+ structured deliverables per year. Calibrated to your geography.',
  });
  usePageTracker('pharma-bd-retainer');

  // Block casual copy / cut / right-click on this page only. (Determined users
  // can still inspect-element — this is a "soft" deterrent for sales leave-behind
  // links, not real DRM.)
  const blockCopy = (e) => { e.preventDefault(); return false; };

  const [heroRef, heroInView] = useInView(0.0);
  const [workRef, workInView] = useInView(0.1);
  const [cadRef, cadInView] = useInView(0.2);
  const [geoRef, geoInView] = useInView(0.2);
  const [vsRef, vsInView] = useInView(0.15);
  const [caseRef, caseInView] = useInView(0.1);

  return (
    <div
      className="bg-[#0A1128] text-white pharma-retainer-no-copy"
      data-testid="pharma-bd-retainer-page"
      onCopy={blockCopy}
      onCut={blockCopy}
      onContextMenu={blockCopy}
      onDragStart={blockCopy}
      style={{
        userSelect: 'none',
        WebkitUserSelect: 'none',
        MozUserSelect: 'none',
        msUserSelect: 'none',
        WebkitTouchCallout: 'none',
      }}
    >
      <PublicNav />

      {/* HERO */}
      <section ref={heroRef} className="relative overflow-hidden pt-32 pb-20 lg:pt-40 lg:pb-28">
        <div className="absolute inset-0">
          <div className="absolute top-1/3 -left-32 w-[600px] h-[600px] rounded-full bg-gradient-to-br from-[#00709B]/15 to-[#70C24C]/5 blur-[120px]" />
          <div className="absolute bottom-0 right-0 w-[500px] h-[500px] rounded-full bg-gradient-to-bl from-[#70C24C]/10 to-transparent blur-[100px]" />
        </div>
        <div className="relative max-w-6xl mx-auto px-6 lg:px-12">
          <div className={heroInView ? 'reveal-up' : 'opacity-0'}>
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/5 border border-white/10 mb-6">
              <span className="w-2 h-2 rounded-full bg-[#70C24C] animate-pulse"></span>
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#70C24C]">For Pharma In-Licensors</span>
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05] max-w-4xl">
              A 12-month retainer that replaces<br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#70C24C] to-[#00709B]">five vendors with one team.</span>
            </h1>
            <p className="mt-8 text-lg sm:text-xl text-[#A1A8C3] font-light max-w-3xl leading-relaxed">
              Subscription database. Conference scout. KOL network. Due-diligence consultant. Deal facilitator.
              We do all five — calibrated to one geography of your choice — under a single fixed retainer.
            </p>
            <div className="mt-10 flex flex-wrap gap-3">
              <Link to="/apply?intent=pharma_retainer"
                    className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full bg-[#70C24C] text-[#0A1128] font-bold hover:bg-[#5fad3b] hover:-translate-y-0.5 transition-all duration-300 shadow-[0_0_30px_rgba(112,194,76,0.25)]"
                    data-testid="hero-request-proposal">
                Request a proposal <ArrowRight className="w-5 h-5" />
              </Link>
              <a href="#case-study"
                 className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full border border-white/15 text-white font-medium hover:bg-white/5 transition-all duration-300"
                 data-testid="hero-jump-case-study">
                See a case study
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* THE 6 WORKSTREAMS */}
      <section ref={workRef} className="py-20 lg:py-28 bg-[#0E1733]" data-testid="workstreams">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className={`max-w-3xl mb-14 ${workInView ? 'reveal-up' : 'opacity-0'}`}>
            <p className="text-xs font-bold uppercase tracking-[0.3em] text-[#00709B] mb-4">What's inside the retainer</p>
            <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight">Six interconnected workstreams.</h2>
            <p className="mt-4 text-base text-[#A1A8C3] font-light">Every retainer covers all six. Frequency, format, and depth are tuned to your geography and BD priorities.</p>
          </div>

          <div className="grid lg:grid-cols-2 gap-5">
            {WORKSTREAMS.map((w, i) => (
              <div key={i}
                   className={`bg-gradient-to-br from-[#111A3A] to-[#0E1733] border border-white/5 rounded-2xl p-7 hover:border-[#00709B]/30 transition-all duration-500 ${workInView ? `reveal-up stagger-${(i % 3) + 1}` : 'opacity-0'}`}
                   data-testid={`workstream-${i}`}>
                <div className="flex items-start gap-4">
                  <div className="w-11 h-11 rounded-xl bg-[#00709B]/15 flex items-center justify-center text-[#00709B] shrink-0 [&>svg]:w-5 [&>svg]:h-5">
                    {w.icon}
                  </div>
                  <div className="flex-1">
                    <h3 className="text-base font-bold text-white mb-2">{w.title}</h3>
                    <p className="text-sm text-[#A1A8C3] font-light leading-relaxed">{w.desc}</p>
                    <div className="mt-3 flex items-start gap-2 text-xs text-[#70C24C]">
                      <CheckCircle2 className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                      <span>{w.out}</span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* 8-Dimension Scoring callout */}
          <div className={`mt-10 bg-[#111A3A] border border-[#00709B]/20 rounded-2xl p-7 ${workInView ? 'reveal-up' : 'opacity-0'}`} data-testid="scoring-callout">
            <div className="flex items-start gap-3 mb-4">
              <Layers className="w-5 h-5 text-[#70C24C] mt-0.5" />
              <div>
                <h3 className="text-base font-bold text-white">The 8-Dimension Scoring Framework</h3>
                <p className="text-sm text-[#A1A8C3] font-light mt-1">Every shortlisted asset is scored across these dimensions in every dossier — same yardstick, every time.</p>
              </div>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5 mt-5">
              {EIGHT_DIMENSIONS.map((d, i) => (
                <div key={i} className="flex items-center gap-2 px-3 py-2.5 rounded-lg bg-[#0A1128] border border-white/5 text-xs text-[#A1A8C3]">
                  <span className="text-[#00709B] font-bold w-4">{i + 1}.</span>
                  <span>{d}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CADENCE RIBBON */}
      <section ref={cadRef} className="py-20 bg-[#0A1128]" data-testid="cadence">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className={`text-center mb-12 ${cadInView ? 'reveal-up' : 'opacity-0'}`}>
            <p className="text-xs font-bold uppercase tracking-[0.3em] text-[#70C24C] mb-4">Cadence</p>
            <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight">90+ structured deliverables. Every year.</h2>
            <p className="mt-3 text-base text-[#A1A8C3] font-light max-w-2xl mx-auto">Predictable rhythm. Designed to keep your BD team continuously informed without information overload.</p>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
            {CADENCE.map((c, i) => (
              <div key={i}
                   className={`text-center p-5 rounded-2xl bg-[#111A3A] border border-white/5 ${cadInView ? `reveal-up stagger-${(i % 6) + 1}` : 'opacity-0'}`}
                   data-testid={`cadence-${i}`}>
                <div className="text-3xl sm:text-4xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-[#70C24C] to-[#00709B]">{c.n}</div>
                <div className="text-[11px] text-[#A1A8C3] font-light uppercase tracking-wide mt-2 leading-tight">{c.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* GEOGRAPHIES */}
      <section ref={geoRef} className="py-20 lg:py-28 bg-[#0E1733]" data-testid="geographies">
        <div className="max-w-6xl mx-auto px-6 lg:px-12">
          <div className={`max-w-3xl mb-12 ${geoInView ? 'reveal-up' : 'opacity-0'}`}>
            <p className="text-xs font-bold uppercase tracking-[0.3em] text-[#00709B] mb-4">Pick your corridor</p>
            <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight">The same retainer. Calibrated to any asset corridor.</h2>
            <p className="mt-4 text-base text-[#A1A8C3] font-light">Every market has its own regulators, conferences, broker networks, and signal channels. We build the in-region intelligence stack so you don't have to.</p>
          </div>
          <div className="flex flex-wrap gap-2.5">
            {GEOGRAPHIES.map((g, i) => (
              <div key={i}
                   className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-[#111A3A] border border-white/10 text-sm font-medium hover:border-[#70C24C]/40 transition-all ${geoInView ? `reveal-up stagger-${(i % 5) + 1}` : 'opacity-0'}`}
                   data-testid={`geo-${i}`}>
                <MapPin className="w-3.5 h-3.5 text-[#70C24C]" />
                <span>{g}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* RETAINER vs SUBSCRIPTION */}
      <section ref={vsRef} className="py-20 lg:py-28 bg-[#0A1128]" data-testid="retainer-vs-sub">
        <div className="max-w-6xl mx-auto px-6 lg:px-12">
          <div className={`max-w-3xl mb-14 ${vsInView ? 'reveal-up' : 'opacity-0'}`}>
            <p className="text-xs font-bold uppercase tracking-[0.3em] text-[#70C24C] mb-4">Why retainer, not subscription</p>
            <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight">Subscriptions stop at the data layer. We don't.</h2>
          </div>
          <div className="grid sm:grid-cols-2 gap-5">
            {RETAINER_VS_SUB.map((r, i) => (
              <div key={i}
                   className={`bg-[#111A3A] border border-white/5 rounded-2xl p-7 hover:border-[#70C24C]/30 transition-all ${vsInView ? `reveal-up stagger-${(i % 4) + 1}` : 'opacity-0'}`}
                   data-testid={`vs-${i}`}>
                <div className="w-10 h-10 rounded-xl bg-[#70C24C]/10 flex items-center justify-center text-[#70C24C] mb-4 [&>svg]:w-5 [&>svg]:h-5">
                  {r.icon}
                </div>
                <h3 className="text-base font-bold text-white mb-2">{r.title}</h3>
                <p className="text-sm text-[#A1A8C3] font-light leading-relaxed">{r.desc}</p>
              </div>
            ))}
          </div>
          <div className="mt-10 p-6 rounded-2xl bg-gradient-to-br from-[#0E1733] to-[#111A3A] border border-white/5 flex items-start gap-4">
            <Clock className="w-5 h-5 text-[#00709B] mt-0.5 shrink-0" />
            <div>
              <div className="text-sm font-bold text-white mb-1">Pricing intent</div>
              <p className="text-sm text-[#A1A8C3] font-light">Fixed quarterly retainer. No per-query fees. No surprise dossier or extra-conference charges. Conferences, platform access, and the AI research copilot are all included. <Link to="/apply?intent=pharma_retainer" className="text-[#70C24C] underline font-medium hover:text-[#5fad3b]">Request a tailored proposal →</Link></p>
            </div>
          </div>
        </div>
      </section>

      {/* CASE STUDY: GREATER CHINA */}
      <section ref={caseRef} id="case-study" className="py-24 lg:py-32 bg-[#0E1733]" data-testid="case-study-china">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className={`max-w-4xl mb-14 ${caseInView ? 'reveal-up' : 'opacity-0'}`}>
            <p className="text-xs font-bold uppercase tracking-[0.3em] text-[#00709B] mb-4">Case Study · What this looks like in practice</p>
            <h2 className="text-3xl sm:text-5xl font-extrabold tracking-tight leading-tight">
              Greater China: a $14.4 B<br />out-licensing corridor.
            </h2>
            <p className="mt-6 text-base sm:text-lg text-[#A1A8C3] font-light leading-relaxed">
              Chinese biotechs filed <strong className="text-white">35% of all global IND applications</strong> in 2024. Cross-border out-licensing from China reached <strong className="text-white">$14.4 B in upfront and milestone value</strong> — a 68% year-on-year jump <span className="text-[#687399]">(BioCentury, January 2025)</span>. By the time these assets surface in English-language databases, the window has often already closed. Below is the eight-source intelligence stack we run for a Greater China retainer — the same architecture we replicate for any other corridor.
            </p>
          </div>

          {/* 8 sources */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-16">
            {CHINA_SOURCES.map((s, i) => (
              <div key={i}
                   className={`bg-[#111A3A] border border-white/5 rounded-xl p-5 hover:border-[#00709B]/30 transition-all ${caseInView ? `reveal-up stagger-${(i % 4) + 1}` : 'opacity-0'}`}
                   data-testid={`china-source-${i}`}>
                <div className="w-9 h-9 rounded-lg bg-[#00709B]/10 flex items-center justify-center text-[#00709B] mb-3 [&>svg]:w-4 [&>svg]:h-4">
                  {s.icon}
                </div>
                <h4 className="text-sm font-bold text-white mb-1.5">{s.title}</h4>
                <p className="text-xs text-[#A1A8C3] font-light leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>

          {/* 4 conferences */}
          <div className={`mb-12 ${caseInView ? 'reveal-up' : 'opacity-0'}`}>
            <h3 className="text-xl font-bold text-white mb-2">Four conferences. Live floor coverage.</h3>
            <p className="text-sm text-[#A1A8C3] font-light mb-6 max-w-3xl">
              Pre-event target lists. On-site one-on-one meeting facilitation. Same-day flash reports. Post-event opportunity briefs within five business days.
            </p>
            <div className="grid sm:grid-cols-2 gap-4">
              {CHINA_CONFERENCES.map((c, i) => (
                <div key={i}
                     className="bg-gradient-to-br from-[#111A3A] to-[#0E1733] border border-white/5 rounded-xl p-5 hover:border-[#70C24C]/30 transition-all"
                     data-testid={`china-conf-${i}`}>
                  <div className="flex items-start justify-between gap-3 mb-2">
                    <h4 className="text-sm font-bold text-white">{c.name}</h4>
                    <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded bg-[#00709B]/15 text-[#00709B] shrink-0">{c.month}</span>
                  </div>
                  <div className="text-xs text-[#70C24C] font-medium mb-2">{c.city}</div>
                  <p className="text-xs text-[#A1A8C3] font-light leading-relaxed">{c.detail}</p>
                </div>
              ))}
            </div>
          </div>

          <div className={`p-6 rounded-2xl bg-[#111A3A] border border-[#70C24C]/15 flex items-start gap-4 ${caseInView ? 'reveal-up' : 'opacity-0'}`}>
            <Globe className="w-5 h-5 text-[#70C24C] mt-0.5 shrink-0" />
            <p className="text-sm text-[#A1A8C3] font-light leading-relaxed">
              <strong className="text-white">We run the equivalent in-region programme for any other corridor.</strong> Japan via PMDA + BioJapan + JBA networks. Korea via MFDS + Bio Korea + K-PIC. India via CDSCO + IPC + India BioForum. EU via EMA + BIO-Europe + Swiss Biotech Day. Same retainer architecture. Different sources, conferences, and BD relationships — calibrated to where the deals actually happen.
            </p>
          </div>
        </div>
      </section>

      {/* ADD-ONS */}
      <section className="py-20 bg-[#0A1128]" data-testid="addons">
        <div className="max-w-5xl mx-auto px-6 lg:px-12">
          <div className="max-w-3xl mb-10">
            <p className="text-xs font-bold uppercase tracking-[0.3em] text-[#00709B] mb-4">Optional Add-Ons</p>
            <h2 className="text-2xl sm:text-3xl font-extrabold tracking-tight">Depth, when you need it.</h2>
            <p className="mt-3 text-base text-[#A1A8C3] font-light">The retainer scope is comprehensive. These additional services can be commissioned at any time during the engagement.</p>
          </div>
          <div className="grid sm:grid-cols-2 gap-2.5">
            {ADDONS.map((a, i) => (
              <div key={i}
                   className="flex items-start gap-3 p-4 rounded-lg bg-[#111A3A] border border-white/5"
                   data-testid={`addon-${i}`}>
                <CheckCircle2 className="w-4 h-4 text-[#70C24C] mt-0.5 shrink-0" />
                <span className="text-sm text-[#A1A8C3]">{a}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 lg:py-32 bg-[#111A3A] relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] rounded-full bg-gradient-to-br from-[#00709B]/15 to-[#70C24C]/15 blur-[100px]" />
        </div>
        <div className="relative max-w-3xl mx-auto px-6 lg:px-12 text-center">
          <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight mb-6">
            One geography. One retainer.<br />
            <span className="text-[#70C24C]">90+ deliverables a year.</span>
          </h2>
          <p className="text-lg text-[#A1A8C3] font-light mb-10 max-w-xl mx-auto">
            Tell us where you're sourcing from. We will scope a 12-month programme calibrated to that corridor and your therapeutic priorities.
          </p>
          <Link to="/apply?intent=pharma_retainer"
                className="inline-flex items-center gap-2 px-10 py-4 rounded-full bg-[#70C24C] text-[#0A1128] text-base font-bold hover:bg-[#5fad3b] hover:-translate-y-0.5 transition-all duration-300 shadow-[0_0_30px_rgba(112,194,76,0.25)]"
                data-testid="cta-request-proposal">
            Request a tailored proposal <ArrowRight className="w-5 h-5" />
          </Link>
          <p className="mt-6 text-xs text-[#687399] uppercase tracking-wider">No commitment. No sales pitch. Honest fit assessment.</p>
        </div>
      </section>

      <PublicFooter />
    </div>
  );
}
